import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import MessageDomain from "entities/domain/conversations/message-domain";

export interface MessageJumpRequest {
  conversationId: number;
  messageId: number;
  requestId: number;
}

// The conversation's media viewer shows one attachment of one loaded message.
export interface MediaToShow {
  messageId: number;
  attachmentIndex: number;
}

interface MessagesState {
  // UI-only state
  emailMessageToShowId: number | undefined; // Store ID instead of instance
  messageJumpRequest: MessageJumpRequest | null;
  messageJumpSequence: number;
  mediaToShow: MediaToShow | null;
}

const initialState: MessagesState = {
  emailMessageToShowId: undefined,
  messageJumpRequest: null,
  messageJumpSequence: 0,
  mediaToShow: null,
};

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    setEmailMessageToShow(
      state,
      action: PayloadAction<MessageDomain | undefined>
    ) {
      state.emailMessageToShowId = action.payload?.id;
    },
    clearEmailMessageToShow(state) {
      state.emailMessageToShowId = undefined;
    },
    jumpToMessage(
      state,
      action: PayloadAction<{ conversationId: number; messageId: number }>
    ) {
      state.messageJumpSequence += 1;
      state.messageJumpRequest = {
        ...action.payload,
        requestId: state.messageJumpSequence,
      };
    },
    clearMessageJump(state, action: PayloadAction<number>) {
      if (state.messageJumpRequest?.requestId === action.payload) {
        state.messageJumpRequest = null;
      }
    },
    setMediaToShow(state, action: PayloadAction<MediaToShow>) {
      state.mediaToShow = action.payload;
    },
    clearMediaToShow(state) {
      state.mediaToShow = null;
    },
    resetStore(state) {
      state.emailMessageToShowId = undefined;
      state.messageJumpRequest = null;
      state.mediaToShow = null;
    },
    // Removed backend data reducers: setIsLoadingMessagesAction, setMessagesReadStatusAction,
    // deleteMessage, appendMessage, clearMessages, setMessagesLastPageLength
    // These are now handled by TanStack Query cache updates
  },
});

// Removed selectors - messages are now accessed via TanStack Query hooks
// Use useConversationMessagesInfinite hook instead

export const {
  setEmailMessageToShow,
  clearEmailMessageToShow,
  jumpToMessage,
  clearMessageJump,
  setMediaToShow,
  clearMediaToShow,
  resetStore,
} = messagesSlice.actions;
export const messagesSliceActions = messagesSlice.actions;
export default messagesSlice.reducer;
